import * as React from 'react';
import { cx } from 'linaria';

import { Typography, useTheme } from '../index';
import * as styles from './badge.styles';

interface BadgeCommonProps extends React.HTMLAttributes<HTMLElement> {
    variant: 'neutral' | 'alert' | 'warning' | 'success' | 'information' | 'catapult-orange';
    testId?: string;
    value?: string | React.ReactNode;
    title: string;
}

interface BadgeNonActionable extends BadgeCommonProps {
    onClick?: never;
    disabled?: never;
}

interface BadgeActionable extends BadgeCommonProps {
    onClick?: () => void;
    disabled?: boolean;
}

export type BadgeProps = BadgeNonActionable | BadgeActionable;

export const Badge = React.forwardRef<HTMLDivElement | HTMLButtonElement, BadgeProps>((props, ref) => {
    const { variant, children, value, onClick, disabled, title, className, ...passthroughProps } = props;

    const theme = useTheme();
    const isDark = theme === 'dark';
    const hasValue = value != null;

    const badgeMarkup = (
        <div className={cx(styles.badge, styles.variants[variant], hasValue ? styles.counter : styles.dot)}>
            {hasValue && (
                <Typography
                    variant="caption-1"
                    color={
                        isDark || variant === 'warning' || variant === 'catapult-orange'
                            ? 'catapult-black'
                            : 'catapult-white'
                    }
                >
                    {value}
                </Typography>
            )}
        </div>
    );

    if (typeof onClick === 'function') {
        return (
            <button
                {...passthroughProps}
                ref={ref as React.ForwardedRef<HTMLButtonElement>}
                className={cx(styles.root, styles.button, className)}
                type="button"
                disabled={disabled}
                onClick={onClick}
                title={title}
                aria-label={title}
            >
                {badgeMarkup}
                {children}
            </button>
        );
    }

    return (
        <div
            {...passthroughProps}
            ref={ref as React.ForwardedRef<HTMLDivElement>}
            className={cx(styles.root, className)}
            title={title}
            aria-label={title}
        >
            {badgeMarkup}
            {children}
        </div>
    );
});

Badge.displayName = 'Badge';
